import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiLogOut, FiMapPin, FiPhone, FiCalendar, FiShoppingBag, FiCreditCard, FiCamera, FiEdit2, FiCheck, FiX, FiLock, FiSend } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { formatPrice, formatDate } from '../utils/constants';
import API from '../api/axios';
import toast from 'react-hot-toast';
import LocationPicker from '../components/LocationPicker';
import './Profile.css';

const Profile = () => {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [editingName, setEditingName] = useState(false);
  const [nameVal, setNameVal] = useState('');
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [nameLoading, setNameLoading] = useState(false);
  const [showLocation, setShowLocation] = useState(false);
  const [showAddressesModal, setShowAddressesModal] = useState(false);

  // Reset Password Modal State
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetOtpSent, setResetOtpSent] = useState(false);
  const [resetOtp, setResetOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleAvatarClick = () => fileRef.current?.click();

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5 MB');
      return;
    }
    setAvatarLoading(true);
    try {
      const form = new FormData();
      form.append('files', file);
      const { data: uploadData } = await API.post('/upload?folder=profile', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      });
      const avatarUrl = uploadData.files?.[0]?.thumbnailLink || uploadData.files?.[0]?.viewLink;
      if (!avatarUrl) throw new Error('No URL in response');
      const { data } = await API.put('/auth/update-profile', { avatar: avatarUrl });
      updateUser(data.user);
      toast.success('Avatar updated!');
    } catch {
      toast.error('Failed to update avatar');
    } finally {
      setAvatarLoading(false);
      e.target.value = '';
    }
  };

  const startEditName = () => {
    setNameVal(user?.name || '');
    setEditingName(true);
  };

  const saveName = async () => {
    if (nameVal.trim().length < 2) {
      toast.error('Name must be at least 2 characters');
      return;
    }
    setNameLoading(true);
    try {
      const { data } = await API.put('/auth/update-profile', { name: nameVal.trim() });
      updateUser(data.user);
      setEditingName(false);
      toast.success('Name updated!');
    } catch {
      toast.error('Failed to update name');
    } finally {
      setNameLoading(false);
    }
  };

  const handleSelectAddress = async (addressId) => {
    try {
      const { data } = await API.put(`/users/address/select/${addressId}`);
      updateUser({ address: data.address });
      toast.success('Active address updated!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to select address');
    }
  };

  const handleDeleteAddress = async (addressId) => {
    try {
      const { data } = await API.delete(`/users/address/${addressId}`);
      updateUser({ savedAddresses: data.savedAddresses });
      toast.success('Address deleted!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete address');
    }
  };

  // Reset Password handlers
  const handleSendResetOTP = async () => {
    setResetLoading(true);
    try {
      await API.post('/auth/forgot-password', { phone: user.phone });
      setResetOtpSent(true);
      toast.success('OTP sent to your phone!');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send OTP');
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setResetLoading(true);
    try {
      await API.post('/auth/reset-password', {
        phone: user.phone,
        otp: resetOtp,
        newPassword
      });
      toast.success('Password reset successfully! Please login with new password.');
      setShowResetModal(false);
      // Clear state
      setResetOtpSent(false);
      setResetOtp('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to reset password');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <FiArrowLeft />
        </button>
        <h1 className="page-title">Profile</h1>
      </div>

      {/* Profile Card */}
      <div className="profile-card">
        {/* Avatar with edit overlay */}
        <div className="profile-avatar-wrap">
          <div className="profile-avatar" onClick={handleAvatarClick} title="Change photo">
            {avatarLoading ? (
              <div className="profile-avatar-spinner" />
            ) : user?.avatar ? (
              <img src={user.avatar} alt="avatar" className="profile-avatar-img" />
            ) : (
              user?.name ? user.name.charAt(0).toUpperCase() : '?'
            )}
          </div>
          <button className="profile-avatar-edit-btn" onClick={handleAvatarClick} disabled={avatarLoading}>
            <FiCamera />
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleAvatarChange}
          />
        </div>

        {/* Editable name */}
        {editingName ? (
          <div className="profile-name-edit">
            <input
              className="profile-name-input"
              value={nameVal}
              onChange={e => setNameVal(e.target.value)}
              maxLength={100}
              autoFocus
            />
            <button className="profile-name-action confirm" onClick={saveName} disabled={nameLoading}>
              <FiCheck />
            </button>
            <button className="profile-name-action cancel" onClick={() => setEditingName(false)}>
              <FiX />
            </button>
          </div>
        ) : (
          <div className="profile-name-row">
            <h2 className="profile-name">{user?.name || 'User'}</h2>
            <button className="profile-name-edit-btn" onClick={startEditName}><FiEdit2 /></button>
          </div>
        )}

        <p className="profile-id">{user?.userId}</p>
        {user?.role === 'admin' && <span className="badge badge-primary">ADMIN</span>}
      </div>

      {/* Details */}
      <div className="profile-details">
        <div className="profile-item">
          <FiPhone className="profile-item-icon" />
          <div>
            <span className="profile-item-label">Phone</span>
            <span className="profile-item-value">+91 {user?.phone}</span>
          </div>
        </div>

        <div className="profile-item">
          <FiCreditCard className="profile-item-icon" />
          <div>
            <span className="profile-item-label">Store Credits</span>
            <span className="profile-item-value" style={{ color: 'var(--accent)' }}>
              {formatPrice(user?.walletBalance || 0)}
            </span>
          </div>
        </div>

        <div className="profile-item">
          <FiShoppingBag className="profile-item-icon" />
          <div>
            <span className="profile-item-label">Total Orders</span>
            <span className="profile-item-value">{user?.numOrders || 0}</span>
          </div>
        </div>

        <div className="profile-item">
          <FiMapPin className="profile-item-icon" />
          <div style={{ flex: 1 }}>
            <span className="profile-item-label">Address</span>
            <span className="profile-item-value">{user?.address?.full || user?.address?.label || 'Not set'}</span>
          </div>
          <button
            className="profile-name-edit-btn"
            onClick={() => setShowAddressesModal(true)}
            style={{ marginLeft: 'auto', alignSelf: 'center' }}
            title="Manage Addresses"
          >
            <FiEdit2 size={16} />
          </button>
        </div>

        <div className="profile-item">
          <FiCalendar className="profile-item-icon" />
          <div>
            <span className="profile-item-label">Member Since</span>
            <span className="profile-item-value">{formatDate(user?.createdAt || new Date())}</span>
          </div>
        </div>
      </div>

      {/* Actions */}
      {/* {user?.role === 'admin' && (
        <button className="btn btn-secondary btn-full" style={{ marginBottom: 10 }} onClick={() => navigate('/admin')}>
          🛠️ Admin Dashboard
        </button>
      )} */}

      <button className="btn btn-outline btn-full" onClick={() => setShowAddressesModal(true)} style={{ marginBottom: 10 }}>
        <FiMapPin /> Saved Addresses
      </button>

      <button className="btn btn-outline btn-full" onClick={() => setShowResetModal(true)} style={{ marginBottom: 10 }}>
        <FiLock /> Change Password
      </button>

      <button className="btn btn-outline btn-full" onClick={() => navigate('/privacy-policy')} style={{ marginBottom: 10 }}>
        📄 Privacy Policy
      </button>

      <button className="btn btn-outline btn-full" onClick={() => navigate('/terms')} style={{ marginBottom: 10 }}>
        📄 Terms & Conditions
      </button>

      <button className="btn btn-danger btn-full" onClick={handleLogout}>
        <FiLogOut /> Logout
      </button>

      {/* Reset Password Modal */}
      {showResetModal && (
        <div className="modal-overlay" onClick={() => setShowResetModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button type="button" className="modal-close" onClick={() => setShowResetModal(false)}>&times;</button>
            <h3 style={{ marginBottom: '1rem' }}>Change Password</h3>
            {!resetOtpSent ? (
              <div>
                <p style={{ marginBottom: '1rem' }}>
                  An OTP will be sent to your registered phone number:<br />
                  <strong>+91 {user?.phone}</strong>
                </p>
                <button
                  className="btn btn-primary btn-full"
                  onClick={handleSendResetOTP}
                  disabled={resetLoading}
                >
                  {resetLoading ? 'Sending...' : 'Send OTP'}
                </button>
              </div>
            ) : (
              <form onSubmit={handleResetSubmit}>
                <p style={{ marginBottom: '1rem' }}>
                  Enter OTP sent to +91 {user?.phone}
                </p>
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <input
                    type="text"
                    value={resetOtp}
                    onChange={(e) => setResetOtp(e.target.value)}
                    placeholder="Enter OTP"
                    maxLength="6"
                    required
                    style={{ width: '100%', padding: '0.75rem', border: '1px solid var(--border)', borderRadius: '8px' }}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="New Password"
                    minLength="6"
                    required
                    style={{ width: '100%', padding: '0.75rem', border: '1px solid var(--border)', borderRadius: '8px' }}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm New Password"
                    required
                    style={{ width: '100%', padding: '0.75rem', border: '1px solid var(--border)', borderRadius: '8px' }}
                  />
                </div>
                <button type="submit" className="btn btn-primary btn-full" disabled={resetLoading}>
                  {resetLoading ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Saved Addresses Modal */}
      {showAddressesModal && (
        <div className="modal-overlay" onClick={() => setShowAddressesModal(false)}>
          <div className="modal-content addresses-modal" onClick={e => e.stopPropagation()}>
            <button type="button" className="modal-close" onClick={() => setShowAddressesModal(false)}>&times;</button>
            <h3 style={{ marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FiMapPin /> Saved Addresses
            </h3>
            
            <div className="saved-addresses-list" style={{ maxHeight: '320px', overflowY: 'auto', marginBottom: '1.5rem', paddingRight: '4px' }}>
              {(!user?.savedAddresses || user.savedAddresses.length === 0) ? (
                <p style={{ color: 'var(--text-muted)', textAlign: 'center', margin: '2rem 0', fontSize: '13px' }}>
                  No saved addresses yet.
                </p>
              ) : (
                user.savedAddresses.map((addr) => {
                  const isActive = user.address && user.address.lat === addr.lat && user.address.lng === addr.lng;
                  return (
                    <div 
                      key={addr._id} 
                      className={`saved-address-card ${isActive ? 'active' : ''}`}
                      style={{
                        padding: '12px',
                        border: '1px solid var(--border)',
                        borderRadius: '8px',
                        marginBottom: '10px',
                        background: isActive ? 'rgba(108, 99, 255, 0.08)' : 'rgba(255, 255, 255, 0.02)',
                        borderColor: isActive ? 'var(--primary)' : 'var(--border)',
                        position: 'relative'
                      }}
                    >
                      <div style={{ paddingRight: '56px' }}>
                        <span className="address-label" style={{ 
                          fontWeight: '700', 
                          fontSize: '11px', 
                          background: 'rgba(255,255,255,0.08)', 
                          padding: '2px 6px', 
                          borderRadius: '4px',
                          marginRight: '6px',
                          textTransform: 'capitalize'
                        }}>{addr.label || 'Home'}</span>
                        {isActive && <span style={{ color: 'var(--primary-light)', fontSize: '11px', fontWeight: '700' }}>Active</span>}
                        <p style={{ fontSize: '13px', margin: '8px 0 0 0', color: 'var(--text-primary)', fontWeight: '600' }}>{addr.line1}</p>
                        {addr.line2 && <p style={{ fontSize: '12px', margin: '2px 0 0 0', color: 'var(--text-muted)' }}>{addr.line2}</p>}
                      </div>
                      
                      <div style={{ 
                        position: 'absolute', 
                        right: '12px', 
                        top: '50%', 
                        transform: 'translateY(-50%)',
                        display: 'flex',
                        gap: '6px'
                      }}>
                        {!isActive && (
                          <button 
                            className="address-action-btn select" 
                            title="Set as Active"
                            onClick={() => handleSelectAddress(addr._id)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: 'var(--primary-light)',
                              cursor: 'pointer',
                              padding: '6px'
                            }}
                          >
                            <FiCheck size={16} />
                          </button>
                        )}
                        <button 
                          className="address-action-btn delete" 
                          title="Delete Address"
                          onClick={() => handleDeleteAddress(addr._id)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#FF6B6B',
                            cursor: 'pointer',
                            padding: '6px'
                          }}
                        >
                          <FiX size={16} />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            
            <button 
              className="btn btn-primary btn-full"
              onClick={() => {
                setShowAddressesModal(false);
                setShowLocation(true);
              }}
            >
              + Add New Address
            </button>
          </div>
        </div>
      )}

      {/* Location Picker */}
      {showLocation && (
        <LocationPicker
          onClose={() => setShowLocation(false)}
        />
      )}

      <div className="fab-spacer"></div>
    </div>
  );
};

export default Profile;
